import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { Formik } from "formik";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerClusterer,
  Marker,
  InfoBox,
} from "@react-google-maps/api";

import { orderByDistance, getPreciseDistance, convertDistance } from "geolib";
import { getDefaultProps, createKey, openInNewTab, getLocationId } from ".";

import Button from "../../elements/button/Button";
import ProductFinderForm from "./ProductFinderForm";
import ProductFinderResults from "./ProductFinderResults";
import Spinner from "../../elements/spinner/Spinner";
import { posthogEvents } from "@col-care/common/utilities/constants";
import { capturePosthogEvent } from "@col-care/common/utilities/index";

import * as hedyStyles from "./variants/productfinder-hedy.module.css";
import * as pressStyles from "./variants/productfinder-press.module.css";
import * as tgsStyles from "./variants/productfinder-tgs.module.css";
import * as amberStyles from "./variants/productfinder-amber.module.css";
import * as seedandstrainStyles from "./variants/productfinder-seedandstrain.module.css";
import * as triplesevenStyles from "./variants/productfinder-tripleseven.module.css";

const ProductFinderBlock = (props) => {
  // console.log({ ProductFinderBlock: props });

  let styles;
  let theme;
  let iconStyle;
  let MapStyles;
  const posthog = props.posthog;

  switch (props.variant) {
    case "hedy":
      styles = hedyStyles.default || hedyStyles;
      theme = "hedyBtn";
      iconStyle =
        "https://images.contentstack.io/v3/assets/bltc2a866f5b4786ae7/blt42103312a8bf49e1/62eaf2144c4bc9109f696966/pin.png";
      break;
    case "amber":
      styles = amberStyles.default || amberStyles;
      theme = "amber";
      iconStyle = "";
      break;
    case "tripleseven":
      styles = triplesevenStyles.default || triplesevenStyles;
      theme = "tripleSeven";
      iconStyle = "";
      break;
    case "seedandstrain":
      styles = seedandstrainStyles.default || seedandstrainStyles;
      theme = "seedandstrain";
      iconStyle = "";
      break;
    case "tgs":
      styles = tgsStyles.default || tgsStyles;
      theme = "tgssmall";
      iconStyle =
        "https://images.contentstack.io/v3/assets/bltc2a866f5b4786ae7/blt5d510c55428b0423/63a0c541e5f72c7ddd632fa0/tgs_pin.png";
      MapStyles = [
        {
          featureType: "all",
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#345543",
            },
          ],
        },
        {
          featureType: "all",
          elementType: "labels.text.stroke",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "administrative",
          elementType: "geometry",
          stylers: [
            {
              visibility: "on",
            },
            {
              color: "#F4EFE9",
            },
            {
              weight: 0.8,
            },
          ],
        },
        {
          featureType: "administrative.country",
          elementType: "geometry.fill",
          stylers: [
            {
              visibility: "on",
            },
          ],
        },
        {
          featureType: "landscape",
          elementType: "geometry",
          stylers: [
            {
              color: "#F4EFE9",
            },
          ],
        },
        {
          featureType: "poi",
          elementType: "all",
          stylers: [
            {
              color: "#F4EFE9",
            },
            {
              lightness: -7,
            },
          ],
        },
        {
          featureType: "poi.park",
          elementType: "all",
          stylers: [
            {
              color: "#F4EFE9",
            },
          ],
        },
        {
          featureType: "road",
          elementType: "geometry.stroke",
          stylers: [
            {
              color: "#083d35",
            },
            {
              weight: 0.3,
            },
            {
              lightness: 10,
            },
          ],
        },
        {
          featureType: "road.highway",
          elementType: "geometry",
          stylers: [
            {
              color: "#69BF4A",
            },
            {
              lightness: -28,
            },
          ],
        },
        {
          featureType: "road.arterial",
          elementType: "geometry",
          stylers: [
            {
              color: "#69BF4A",
            },
            {
              visibility: "on",
            },
            {
              lightness: -15,
            },
          ],
        },
        {
          featureType: "road.local",
          elementType: "geometry",
          stylers: [
            {
              color: "#2ecc71",
            },
            {
              lightness: -18,
            },
          ],
        },
        {
          featureType: "transit",
          elementType: "geometry",
          stylers: [
            {
              color: "#2ecc71",
            },
            {
              lightness: -34,
            },
          ],
        },
        {
          featureType: "transit.station.airport",
          elementType: "geometry.fill",
          stylers: [
            {
              visibility: "simplified",
            },
          ],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [
            {
              color: "#1a3c34",
            },
          ],
        },
      ];
      break;
    case "press":
      styles = pressStyles.default || pressStyles;
      theme = "pressBtn";
      iconStyle =
        "https://images.contentstack.io/v3/assets/bltc2a866f5b4786ae7/blt7360f3b73bb377cd/6387ad52303d7a10a114a7f2/pin.png";
      MapStyles = [
        {
          featureType: "all",
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#ffffff",
            },
          ],
        },
        {
          featureType: "all",
          elementType: "labels.text.stroke",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "administrative",
          elementType: "geometry",
          stylers: [
            {
              visibility: "on",
            },
            {
              color: "#333739",
            },
            {
              weight: 0.8,
            },
          ],
        },
        {
          featureType: "administrative.country",
          elementType: "geometry.fill",
          stylers: [
            {
              visibility: "on",
            },
          ],
        },
        {
          featureType: "landscape",
          elementType: "geometry",
          stylers: [
            {
              color: "#57b094",
            },
          ],
        },
        {
          featureType: "poi",
          elementType: "all",
          stylers: [
            {
              color: "#2ecc71",
            },
            {
              lightness: -7,
            },
          ],
        },
        {
          featureType: "poi.park",
          elementType: "all",
          stylers: [
            {
              color: "#2ecc71",
            },
          ],
        },
        {
          featureType: "road",
          elementType: "geometry.stroke",
          stylers: [
            {
              color: "#083d35",
            },
            {
              weight: 0.3,
            },
            {
              lightness: 10,
            },
          ],
        },
        {
          featureType: "road.highway",
          elementType: "geometry",
          stylers: [
            {
              color: "#57b094",
            },
            {
              lightness: -28,
            },
          ],
        },
        {
          featureType: "road.arterial",
          elementType: "geometry",
          stylers: [
            {
              color: "#57b094",
            },
            {
              visibility: "on",
            },
            {
              lightness: -15,
            },
          ],
        },
        {
          featureType: "road.local",
          elementType: "geometry",
          stylers: [
            {
              color: "#2ecc71",
            },
            {
              lightness: -18,
            },
          ],
        },
        {
          featureType: "transit",
          elementType: "geometry",
          stylers: [
            {
              color: "#2ecc71",
            },
            {
              lightness: -34,
            },
          ],
        },
        {
          featureType: "transit.station.airport",
          elementType: "geometry.fill",
          stylers: [
            {
              visibility: "simplified",
            },
          ],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [
            {
              color: "#1a3c34",
            },
          ],
        },
      ];
      break;
    default:
      styles = tgsStyles.default || tgsStyles;
      theme = "tgsBtn";
      iconStyle;
      break;
  }

  const state = !props.mockLocations
    ? useSelector((state) => state)
    : undefined;

  // if (state) {
  //   console.log("state found; using real data", state);
  // } else {
  //   console.log("no state; using mock data");
  // }

  let sortedCoordinates;

  const defaultProps = getDefaultProps({
    id: "product-finder",
    center: props.center,
    zoom: props.zoom,
    googleMapsApiKey: props.googleMapsApiKey,
  });
  const { isLoaded } = useJsApiLoader(defaultProps);

  const [zoom, setZoom] = useState(defaultProps.zoom || 4);

  const [map, setMap] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [currentCenter, setCurrentCenter] = useState(defaultProps.center);
  const [currentZip, setCurrentZip] = useState("");
  const [currentMarker, setCurrentMarker] = useState(null);
  const [zipTail, setZipTail] = useState("");
  const [closestStore, setClosestStore] = useState(null);
  const [topThreeStores, setTopThreeStores] = useState({});

  const [classes, setClasses] = useState(false);
  const [activeClasses, setActiveClasses] = useState();

  const resultsRef = useRef(null);

  const locations = props.mockLocations
    ? props.mockLocations
    : state.hydrate.databagData.locations;

  const locationsByUrl = useMemo(() => {
    return locations
      ? locations.reduce((a, v) => {
          a[v.url] = v;
          return a;
        }, {})
      : {};
  }, [locations]);

  const unsortedCoordinates = locations
    ? locations.reduce((a, v) => {
        if (!v.content.geolocation.lat || !v.content.geolocation.lng) {
          return a;
        }
        a.push({
          url: v.url,
          title: v.title,
          // adding these for convenience
          lat: v.content.geolocation.lat,
          lng: v.content.geolocation.lng,
          content: v.content,
        });
        return a;
      }, [])
    : [];
  // }, [locations]);

  const getWrappedMap = createGoogleMap(
    styles,
    theme,
    MapStyles,
    iconStyle,
    defaultProps,
    unsortedCoordinates,
    currentCenter,
    setCurrentCenter,
    currentMarker,
    setCurrentMarker,
    zoom,
    setZoom,
    map,
    setMap,
    setActiveClasses,
    resultsRef,
    topThreeStores,
    setTopThreeStores,
    setApiData,
    posthog,
    props.cartData
  );

  // useEffect(() => {
  //   console.log(currentCenter)
  // }, [currentCenter]);

  useEffect(() => {
    if (apiData && apiData.coordinates) {
      const coordSet = apiData.coordinates[zipTail];

      if (coordSet) {
        const _topThreeStores = {};
        sortedCoordinates = orderByDistance(coordSet, unsortedCoordinates);

        if (!sortedCoordinates[0]) {
          return <h4>Nothing nearby!</h4>;
        }

        // const thisCurrentCenter = currentCenter || apiData.coordinates;
        const thisCurrentCenter = apiData.coordinates;

        if (thisCurrentCenter) {
          _topThreeStores[0] = {
            url: sortedCoordinates[0].url,
            coordinates: {
              lat: sortedCoordinates[0].lat,
              lng: sortedCoordinates[0].lng,
            },
            distance: convertDistance(
              getPreciseDistance(thisCurrentCenter, sortedCoordinates[0]),
              "mi"
            ),
          };

          if (topThreeStores[0]) {
            setClosestStore(locationsByUrl[topThreeStores[0].url]);
          }

          if (sortedCoordinates[1]) {
            _topThreeStores[1] = {
              url: sortedCoordinates[1].url,
              coordinates: {
                lat: sortedCoordinates[1].lat,
                lng: sortedCoordinates[1].lng,
              },
              distance: convertDistance(
                getPreciseDistance(thisCurrentCenter, sortedCoordinates[1]),
                "mi"
              ),
            };
          }

          if (sortedCoordinates[2]) {
            _topThreeStores[2] = {
              url: sortedCoordinates[2].url,
              coordinates: {
                lat: sortedCoordinates[2].lat,
                lng: sortedCoordinates[2].lng,
              },
              distance: convertDistance(
                getPreciseDistance(thisCurrentCenter, sortedCoordinates[2]),
                "mi"
              ),
            };
          }
          if (
            _topThreeStores[0] &&
            sortedCoordinates[0] &&
            _topThreeStores[0].coordinates.lat != thisCurrentCenter.lat
          ) {
            setCurrentCenter(_topThreeStores[0].coordinates);
            setCurrentMarker(_topThreeStores[0]);
            map.panTo(_topThreeStores[0].coordinates);
            setTopThreeStores(_topThreeStores);
            setActiveClasses(_topThreeStores[0]?.url);
          }
        }
      } else {
        setCurrentCenter(null);
      }
    }
  }, [apiData]);

  return isLoaded ? (
    <section
      className={`${styles.product_category_container}`}
      id={"product-finder-section"}
    >
      <div
        style={{ display: props.hidden === true ? "none" : "flex" }}
        id={styles.two_columns}
      >
        <div id={styles.map_styles}>
          {isLoaded ? getWrappedMap : <h2>Please enter a valid ZIP code.</h2>}
        </div>
        <div
          id={styles.search_section}
          className={`${classes && styles.scroll}`}
        >
          <Formik
            initialValues={{
              zip: null,
            }}
            validate={(values) => {
              const errors = {};

              if (!values.zip) {
                errors.zip = "Please enter a ZIP code";
              }

              if (values.zip && values.zip.length < 3) {
                errors.zip = "Need at least 3 characters";
              }

              return errors;
            }}
            onSubmit={(values, { setSubmitting, setStatus }) => {
              setCurrentZip(values.zip);

              capturePosthogEvent({
                eventName: posthogEvents.PRODUCT_FINDER,
                title: `Searched for zip code ${values.zip}`,
                posthog,
                databag: {
                  cartData: props?.cartData,
                },
              });

              const strLength = values.zip.length || 0;
              setZoom(12);
              if (strLength >= 3 && strLength <= 5) {
                // @todo: gotta have a list of possible hostnames
                const path = `${props.hostname}${
                  props.apiPath
                }/${values.zip.substring(0, 3)}/page-data.json`;

                const fetchOptions = {
                  method: "GET",
                  mode: "cors",
                  cache: "default",
                };

                const fetchRequest = new Request(path, fetchOptions);

                fetch(fetchRequest)
                  .then((response) => {
                    response.json().then((data) => {
                      if (
                        data.result.pageContext.content?.apiData?.coordinates
                      ) {
                        // console.log("!!", data.result.pageContext.content?.apiData?.coordinates);
                        setApiData(data.result.pageContext.content.apiData);
                      } else {
                        console.log("no API data found!");
                        setApiData(null);
                      }
                    });
                  })
                  .catch((err) => {
                    setApiError(err);
                  });
              }

              return false;
            }}
          >
            {({
              values,
              errors,
              touched,
              resetForm,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              status,
            }) => {
              return (
                <ProductFinderForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  handleSubmit={handleSubmit}
                  resetForm={resetForm}
                  setZipTail={setZipTail}
                  currentZip={currentZip}
                  isSubmitting={isSubmitting}
                  status={status}
                  setZoom={setZoom}
                  map={map}
                  setCurrentCenter={setCurrentCenter}
                  setCurrentMarker={setCurrentMarker}
                  setActiveClasses={setActiveClasses}
                  apiData={apiData}
                  setApiData={setApiData}
                  setTopThreeStores={setTopThreeStores}
                  defaultProps={defaultProps}
                  {...props}
                />
              );
            }}
          </Formik>

          <div id={styles.product_results} ref={resultsRef}>
            <ProductFinderResults
              apiData={apiData}
              currentZip={currentZip}
              currentCenter={currentCenter}
              locationsByUrl={locationsByUrl}
              unsortedCoordinates={unsortedCoordinates}
              closestStore={closestStore}
              map={map}
              setClosestStore={setClosestStore}
              setCurrentCenter={setCurrentCenter}
              setCurrentMarker={setCurrentMarker}
              classes={activeClasses}
              setClasses={setActiveClasses}
              topThreeStores={topThreeStores}
              setZoom={setZoom}
              variant={props.variant}
              cartData={props?.cartData}
              doSetCartData={props?.actions?.session?.doSetCartData}
              setShowLocationUpdateModal={props?.setShowLocationUpdateModal}
              posthog={posthog}
            />
          </div>
        </div>
      </div>
    </section>
  ) : (
    <div style={{ height: "620px", position: "relative" }}>
      <Spinner />
    </div>
  );
};

export default ProductFinderBlock;
/* let MapStyles = [
  {
      "featureType": "all",
      "elementType": "labels.text.fill",
      "stylers": [
          {
              "color": "#e8b343"
          }
      ]
  },
  {
      "featureType": "all",
      "elementType": "labels.text.stroke",
      "stylers": [
          {
              "visibility": "off"
          }
      ]
  },
  {
      "featureType": "administrative",
      "elementType": "geometry",
      "stylers": [
          {
              "visibility": "on"
          },
          {
              "color": "#333739"
          },
          {
              "weight": 0.8
          }
      ]
  },
  {
      "featureType": "administrative.country",
      "elementType": "geometry.fill",
      "stylers": [
          {
              "visibility": "on"
          }
      ]
  },
  {
      "featureType": "landscape",
      "elementType": "geometry",
      "stylers": [
          {
              "color": "#57b094"
          }
      ]
  },
  {
      "featureType": "poi",
      "elementType": "all",
      "stylers": [
          {
              "color": "#2ecc71"
          },
          {
              "lightness": -7
          }
      ]
  },
  {
      "featureType": "poi.park",
      "elementType": "all",
      "stylers": [
          {
              "color": "#2ecc71"
          }
      ]
  },
  {
      "featureType": "road",
      "elementType": "geometry.stroke",
      "stylers": [
          {
              "color": "#083d35"
          },
          {
              "weight": 0.3
          },
          {
              "lightness": 10
          }
      ]
  },
  {
      "featureType": "road.highway",
      "elementType": "geometry",
      "stylers": [
          {
              "color": "#57b094"
          },
          {
              "lightness": -28
          }
      ]
  },
  {
      "featureType": "road.arterial",
      "elementType": "geometry",
      "stylers": [
          {
              "color": "#57b094"
          },
          {
              "visibility": "on"
          },
          {
              "lightness": -15
          }
      ]
  },
  {
      "featureType": "road.local",
      "elementType": "geometry",
      "stylers": [
          {
              "color": "#2ecc71"
          },
          {
              "lightness": -18
          }
      ]
  },
  {
      "featureType": "transit",
      "elementType": "geometry",
      "stylers": [
          {
              "color": "#2ecc71"
          },
          {
              "lightness": -34
          }
      ]
  },
  {
      "featureType": "transit.station.airport",
      "elementType": "geometry.fill",
      "stylers": [
          {
              "visibility": "simplified"
          }
      ]
  },
  {
      "featureType": "water",
      "elementType": "geometry",
      "stylers": [
          {
              "color": "#1a3c34"
          }
      ]
  }
]
*/

const createGoogleMap = (
  styles,
  theme,
  MapStyles,
  iconStyle,
  defaultProps,
  unsortedCoordinates,
  currentCenter,
  setCurrentCenter,
  currentMarker,
  setCurrentMarker,
  zoom,
  setZoom,
  map,
  setMap,
  setActiveClasses,
  resultsRef,
  topThreeStores,
  setTopThreeStores,
  setApiData,
  posthog,
  cartData
) => {
  // console.log('render map', unsortedCoordinates)

  return (
    <GoogleMap
      clickableIcons={false}
      mapContainerStyle={{
        width: "100%",
        height: "100%",
      }}
      options={{
        styles: MapStyles,
      }}
      center={currentCenter}
      zoom={zoom}
      onZoomChanged={() => {
        if (map) {
          setZoom(map.zoom);
        }
      }}
      onLoad={(map) => {
        map.setOptions({
          draggableCursor: "default",
          draggingCursor: "pointer",
        });

        setMap(map);

        map.setCenter(defaultProps.center);
      }}
    >
      <MarkerClusterer zoomOnClick={true}>
        {(clusterer) =>
          getClusters(
            styles,
            theme,
            MapStyles,
            iconStyle,
            clusterer,
            unsortedCoordinates,
            currentMarker,
            setCurrentMarker,
            setCurrentCenter,
            map,
            setActiveClasses,
            zoom,
            setZoom,
            resultsRef,
            topThreeStores,
            setTopThreeStores,
            setApiData,
            posthog,
            cartData
          )
        }
      </MarkerClusterer>
    </GoogleMap>
  );
};

const getClusters = (
  styles,
  theme,
  MapStyles,
  iconStyle,
  clusterer,
  unsortedCoordinates,
  currentMarker,
  setCurrentMarker,
  setCurrentCenter,
  map,
  setActiveClasses,
  zoom,
  setZoom,
  resultsRef,
  topThreeStores,
  setTopThreeStores,
  setApiData,
  posthog,
  cartData
) => {
  return unsortedCoordinates.map((location, index) => {
    return (
      <Marker
        key={"marker-" + createKey(location) + index}
        position={location}
        clusterer={clusterer}
        icon={iconStyle}
        scale="0.05"
        onClick={(e) => {
          // setZoom(12);
          setCurrentMarker(location);
          setCurrentCenter({
            lat: location?.lat,
            lng: location?.lng,
          });
          // map.panTo({
          //   lat: location?.lat,
          //   lng: location?.lng,
          // });
          setActiveClasses(location?.url);

          const ele = document.getElementById(getLocationId(location?.url));
          ele &&
            resultsRef.current.scrollTo({
              top: ele.offsetTop - resultsRef.current.offsetTop,
              behavior: "smooth",
            });

          if (Object.keys(topThreeStores)[0]) {
            const checkTopThree = Object.keys(topThreeStores).filter((v, k) => {
              if (topThreeStores[v].url === location.url) {
                return true;
              }
            });
            if (!checkTopThree[0]) {
              setActiveClasses(false);
              setApiData(null);
              setTopThreeStores({});
              // resetForm()
              const ele = document.getElementById(getLocationId(location?.url));
              ele &&
                resultsRef.current.scrollTo({
                  top: ele.offsetTop - resultsRef.current.offsetTop,
                  behavior: "smooth",
                });
              setActiveClasses(location?.url);
            }
          }
        }}
      >
        {currentMarker?.url === location.url && (
          <InfoBox
            options={{
              enableEventPropagation: true,
              disableAutoPan: false,
              pixelOffset: new google.maps.Size(-140, 0),

              boxStyle: {
                width: "240px",
                padding: "10px",
                position: "relative",
              },
              closeBoxMargin: "12px 10px 0px 2px",
              zIndex: null,
              closeBoxURL:
                "https://images.contentstack.io/v3/assets/bltc2a866f5b4786ae7/blte6c8561f6ca4f167/62f52d937ef11d3a32a97b91/hedy-close.png",
            }}
            key={"infobox-" + createKey(location)}
            position={location}
            visible={true}
            onCloseClick={() => {
              setCurrentMarker(null);
              // setZoom(zoom - 1)
              // setCurrentCenter(defaultProps.center)
              // map.panTo(defaultProps.center);
              setActiveClasses(false);
            }}
            id={styles.infoBox}
            onLoad={() => {
              // @todo : Rerendering three times so need to work on this
              // console.log("infobox shown");
              capturePosthogEvent({
                eventName: `${posthogEvents.PRODUCT_FINDER} : Infobox shown`,
                title: location?.content?.brand,
                databag: {
                  cartData: cartData,
                  address:
                    location?.content?.address_1 +
                    " " +
                    location?.content?.city +
                    " " +
                    location?.content?.state,
                  id: location?.content?.id,
                },
                posthog,
              });
            }}
          >
            <div className={styles.infoBox}>
              <div
                onClick={(e) => {
                  // console.log("hacked info box");
                  capturePosthogEvent({
                    eventName: `${posthogEvents.PRODUCT_FINDER} : Infobox clicked`,
                    title: location?.content?.brand,
                    databag: {
                      cartData: cartData,
                      address:
                        location?.content?.address_1 +
                        " " +
                        location?.content?.city +
                        " " +
                        location?.content?.state,
                      id: location?.content?.id,
                    },
                    posthog,
                  });
                }}
                id="infobox"
              >
                <h4>{`${location?.content?.brand}`}</h4>
                <span>{`${location?.content?.address_1}`}</span>
                <span>
                  {`${location?.content?.city}`},{" "}
                  {`${location?.content?.state}`}
                </span>
                {location?.content?.telephone && (
                  <span>{`${location?.content?.telephone}`}</span>
                )}
                <Button
                  theme={theme}
                  label="directions"
                  onClick={(e) => {
                    e.stopPropagation();
                    openInNewTab(`${location?.content?.directions}`);
                    capturePosthogEvent({
                      eventName: `${posthogEvents.PRODUCT_FINDER} : Infobox directions button clicked`,
                      title: location?.content?.brand,
                      databag: {
                        cartData: cartData,
                        address:
                          location?.content?.address_1 +
                          " " +
                          location?.content?.city +
                          " " +
                          location?.content?.state,
                        id: location?.content?.id,
                      },
                      posthog,
                    });
                  }}
                />
                {(() => {
                  if (zoom <= 12) {
                    return (
                      <Button
                        theme={theme}
                        id="map-zoom-button"
                        label="zoom"
                        onClick={(e) => {
                          e.stopPropagation();
                          setZoom(14);
                          capturePosthogEvent({
                            eventName: `${posthogEvents.PRODUCT_FINDER} : Infobox Zoom button clicked`,
                            title: location?.content?.brand,
                            databag: {
                              cartData: cartData,
                              address:
                                location?.content?.address_1 +
                                " " +
                                location?.content?.city +
                                " " +
                                location?.content?.state,
                              id: location?.content?.id,
                            },
                            posthog,
                          });
                        }}
                        // disabled={ zoom == 12 ? true : false }
                      />
                    );
                  }
                })()}
              </div>
            </div>
          </InfoBox>
        )}
      </Marker>
    );
  });
};
